import {
  getActivitySummary,
  postPriorActivitiesDisplayed,
  postActivityAcknowledged,
  postToggleFollow,
} from './requests';

/**
 * @return {object}
 */
const requestOlderActivities = () => {
  return {
    type: 'REQUEST_OLDER_ACTIVITIES',
  };
};

/**
 * @param {object} response - the activity summary from the server
 * @param {number} pageSize - number of activities requested
 * @return {object}
 */
const receiveOlderActivities = (response, pageSize) => {
  return {
    type: 'RECEIVE_OLDER_ACTIVITIES',
    payload: {
      pageSize,
      ...response,
    },
    error: false,
  };
};

/**
 * @param {object} response - the activity summary from the server
 * @return {object}
 */
const receiveNewActivities = (response) => {
  return {
    type: 'RECEIVE_NEW_ACTIVITIES',
    payload: response,
    error: false,
  };
};

/**
 * @param {number} userId - the user ID being followed
 * @return {object}
 */
const startToggleFollow = (userId) => {
  return {
    type: 'START_TOGGLE_FOLLOW',
    payload: userId,
  };
};

/**
 * @param {number} userId - the user ID being followed
 * @return {object}
 */
const completeToggleFollow = (userId) => {
  return {
    type: 'COMPLETE_TOGGLE_FOLLOW',
    payload: userId,
  };
};

/**
 * @param {string} id - the activity to show as acknowledged in app state/ui
 * @return {object}
 */
const showActivityAcknowledged = (id) => {
  return {
    type: 'ACTIVITY_ACKNOWLEDGED',
    payload: id,
  };
};

/**
 * Request all activities newer than the newest received activity for current user.
 * When the request completes, dispatch an action to handle the response.
 * This is used for polling.
 *
 * @return {function}
 */
const fetchNewActivities = () => (dispatch, getState) => {
  const activities = getState().activities;
  const numActivities = activities.length;
  const newestId = numActivities ? activities[0].id : null;

  // Pass a page size of null to force controller to return *all* new
  const count = null;

  return getActivitySummary(count, null, newestId).then((response) => {
    dispatch(receiveNewActivities(response));
  });
};

/**
 * Dispatch an action to indicate the app is loading more data.
 * Request next pageSize's worth of activities older than the oldest received
 * activity. When the request completes, dispatch an action to handle the response.
 *
 * @param {number} pageSize - the number of activities to request
 * @return {function}
 */
const fetchOlderActivities = (pageSize) => (dispatch, getState) => {
  const activities = getState().activities;
  const numActivities = activities.length;
  const oldestId = numActivities ? activities[numActivities - 1].id : null;

  dispatch(requestOlderActivities());
  return getActivitySummary(pageSize, oldestId).then((response) => {
    dispatch(receiveOlderActivities(response, pageSize));
  });
};

/**
 * Tell the server the current user has acknowledged (clicked on) an
 * activity. Update the view/ui state to reflect that when the request
 * succeeds.
 *
 * @param {string} id - the id the user clicked on
 * @return {object}
 */
const markActivityAcknowledged = (id) => (dispatch) => {
  return postActivityAcknowledged(id).then((response) => {
    dispatch(showActivityAcknowledged(id));
  });
};

/**
 * Tell the server the current user wants to follow the given userId
 *
 * @param {number} userId
 * @return {function}
 */
const toggleFollow = (userId) => {
  return (dispatch) => {
    dispatch(startToggleFollow(userId));
    return postToggleFollow(userId).then((payload) => {
      dispatch(completeToggleFollow(userId));
    });
  };
};

/**
 * Send its id to the server to record that it and everything older than it
 * was displayed to the user.
 * Though we initiate this async request, we immediately update the
 * app/ui state so that the red dot disappears right away.
 *
 * @return {object} - no need for a thunk here!
 */
const markAllActivitiesDisplayed = () => (dispatch, getState) => {
  const newestActivity = getState().activities[0];

  if (newestActivity && !newestActivity.displayed) {
    postPriorActivitiesDisplayed(newestActivity.id);
  }
};

const showAllActivitiesDisplayed = () => {
  return {
    type: 'ACTIVITIES_DISPLAYED',
  };
};

export {
  fetchNewActivities,
  fetchOlderActivities,
  markAllActivitiesDisplayed,
  showAllActivitiesDisplayed,
  markActivityAcknowledged,
  toggleFollow,
};
